import React, { useState, useRef, useEffect, useMemo, useReducer } from 'react';
import { useNavigate } from 'react-router';
import AppContext from './AppContext';
import { askLLM } from '../ai/askLLM';
import { authedFetch, authHeadersForUrl } from '../ai/authedFetch';
import { parseLlmJson } from '../ai/parseLlmJson';
import { sanitizeNlMeta, normalizeDecomposerOutput } from '../ai/sanitizeNlMeta';
import { buildDecomposerPrompt } from '../ai/prompts/initialInstructions';
import { MODEL_REGISTRY } from '../ai/registry/modelRegistry';
import { getFilteredCategories } from '../ai/registry/categoryRegistry';
import { classifyDomains } from '../ai/classification/domainClassifier';
import { normalizeParsedWithObjectives } from '../controllers/planning';
import { resolveSpec } from '../ai/router/specResolver';
import { useAuth } from './AuthContext';
import {
  pushRun as dbPushRun,
  startRun as dbStartRun,
  patchRun as dbPatchRun,
  loadRuns as dbLoadRuns,
  setSavedFlag as dbSetSavedFlag,
  loadSavedCandidates as dbLoadSavedCandidates,
  addNoteMessage as dbAddNoteMessage,
  editNoteMessage as dbEditNoteMessage,
  deleteNoteMessage as dbDeleteNoteMessage,
  loadNotes as dbLoadNotes,
  saveAnalyses as dbSaveAnalyses,
  pushRunCandidates as dbPushRunCandidates,
  framesToCandidateDocs as dbFramesToCandidateDocs,
  loadRunCandidates as dbLoadRunCandidates,
  setCandidateStage as dbSetCandidateStage,
} from '../firebase/db';
import useToolsRunner from '../hooks/useToolsRunner';
import { manualFormToRows } from '../ai/rows/manualRows';
import { routeAndPrefill } from '../ai/rows/prefillRows';
import { cloneJson, normalizeReplayRow } from '../ai/rows/rowContract';
import { rowsFromLegacyPlan } from '../ai/rows/legacyRows';
import { reviewReducer, INITIAL_REVIEW_STATE, selectCanExecute, selectIsDraft } from '../state/reviewReducer';
import { serializeReviewState, restoreReviewState, rebuildLegacyReview } from '../state/reviewStateStorage';
import { nothingRunnableMessage } from '../ai/rows/prepareRows';
import { useChemistry } from './ChemistryContext';
import { getOrgLimits, PLAN_LIMITS } from '../config/planLimits';
import { mattergenChunkError, DEFAULT_MATTERGEN_CHUNK_SIZE } from '../components/mattergen/mattergenJobs';
import { builtinMattergenCheckpoint, MATTERGEN_BUILTIN_CHECKPOINTS } from '../components/mattergen/mattergenCheckpoints';
import { computeCustomModelBaseUrl, computeCustomModelAuthHeaders, customModelResultHeaders, dispatchTargetForBase, isPoolableDispatchTarget } from './customModelSelection';
import { SUPERADMIN_ORG_ID } from '../config/superadminOrg';
import { normalizeModelPolicy } from '../config/capabilities';
import { BUILTIN_RANKING_WEIGHTS, validRankingWeights } from '../config/ranking';
import { GT4SD_RT_TARGET_RANGE } from '../config/gt4sdCheckpoints';
import { fileModalErrorMessage } from '../utils/fileModalError';
import { loadExtxyzJobs } from '../utils/extxyzJobs';

export default function AppProvider({ children }) {
  const navigate = useNavigate();
  const { user, org, effectiveOrg, orgMember, isSuperAdmin, capabilities: authCapabilities } = useAuth() || {};
  const modelPolicy = authCapabilities?.modelPolicy;
  // Superadmin sessions write under SUPERADMIN_ORG_ID; AuthProvider's effectiveOrg is
  // that org's doc, so the consent gate below reads the org the writes land in.
  const orgId = orgMember?.orgId || (isSuperAdmin ? SUPERADMIN_ORG_ID : null);
  const allowedCategories = isSuperAdmin ? null : (org?.allowedCategories || null);

  // ── Pilot tier gating ──────────────────────────────────────────────────────
  const { plan: orgPlan, limits: planLimits } = getOrgLimits(org, { superadmin: isSuperAdmin });
  const isPilot = orgPlan === 'pilot';
  // Part 1a: the per-run candidate max the row builders and the Execute backstop read.
  const planMax = Number(planLimits?.maxCandidatesPerRun) || PLAN_LIMITS.pilot.maxCandidatesPerRun;

  // Core UI/plan state
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(() => {
    try { const j = localStorage.getItem('plan_v1'); return j ? JSON.parse(j) : null; } catch (_) { return null; }
  });
  const [loadingMessage, setLoadingMessage] = useState('');
  const [toolResults, setToolResults] = useState(() => {
    try { const j = localStorage.getItem('tool_results_v1'); return j ? JSON.parse(j) : null; } catch (_) { return null; }
  });
  const [toolResultsTs, setToolResultsTs] = useState(() => {
    try {
      const stored = localStorage.getItem('tool_results_ts_v1');
      if (stored) return stored;
      // Backfill from most recent history entry for existing sessions
      const h = localStorage.getItem('run_history_v1');
      if (h) { const parsed = JSON.parse(h); return parsed[0]?.ts || null; }
      return null;
    } catch (_) { return null; }
  });
  // Part 1a (D36): the run whose results are on screen, { runId, rows, rankingWeights }:
  // the rows it executed and the weights its crystals are ranked with. Mirrored to
  // localStorage with the results it describes, so it survives a refresh with them.
  const [resultsRun, setResultsRun] = useState(() => {
    try { const j = localStorage.getItem('results_run_v1'); return j ? JSON.parse(j) : null; } catch (_) { return null; }
  });
  const mattergenRef = useRef(null);
  const fm4mRef = useRef(null);
  const gt4sdRef = useRef(null);
  const latestRunIdRef = useRef((() => { try { return localStorage.getItem('latest_run_id_v1') || null; } catch (_) { return null; } })()); // tracks current run ID for result persistence
  const lastSubmitRef = useRef({}); // stores { userQuery, classifierResult, selectedCategory, mode } for startRunDoc/finishRunDoc
  // D8 (durability audit 2026-08-20): id of the draft run doc backing the
  // CURRENT plan-review window (startRunDoc(..., 'draft') below), or null
  // when there isn't one (manual runs and rerunRun never review a plan first,
  // so they never set this). executePlannedSteps reads it to PROMOTE that
  // same doc to 'running' instead of writing a second one; resumeDraft sets
  // it when reopening an abandoned draft from History, so the doc that gets
  // promoted is provably the one the user resumed, not a fresh one.
  const draftRunIdRef = useRef(null);
  // Part 1a (2.1 step 0): the pending debounced reviewState patch, { timer, run }, or
  // null. run() writes what it captured when it was scheduled (the draft id and the
  // serialized review) to the run doc and the local history row. A ref change does not
  // re-run the debounce effect, so every site that leaves the current review FLUSHES a
  // pending patch onto its own draft (clearReview, resumeDraft) rather than dropping an
  // edit made inside the 600 ms window. Only the Execute promotion cancels one, because
  // the promotion patch carries the final review.
  const draftReviewPatchRef = useRef(null);
  const cancelDraftReviewPatch = () => {
    if (draftReviewPatchRef.current) {
      clearTimeout(draftReviewPatchRef.current.timer);
      draftReviewPatchRef.current = null;
    }
  };
  const flushDraftReviewPatch = () => {
    const pending = draftReviewPatchRef.current;
    if (!pending) return;
    clearTimeout(pending.timer);
    draftReviewPatchRef.current = null;
    pending.run();
  };
  // The draft write (startRunDoc 'draft') and resumeDraft both leave a review that is
  // exactly what the doc already holds. That review change still runs the debounce effect
  // once, and without this flag it would schedule a redundant patch of the same value. Set
  // right after either; the effect consumes it (skips scheduling) exactly once, so every
  // later edit patches normally.
  const skipNextReviewPatchRef = useRef(false);
  const [mode, setMode] = useState('nl');
  const [selectedCategory, setSelectedCategory] = useState(null); // id from CATEGORY_REGISTRY or null
  const [progress, setProgress] = useState({});
  const [history, setHistory] = useState([]);
  // Part 1a (contract 1.8): the plan review. Its rows are what NL Execute runs.
  const [review, dispatchReview] = useReducer(reviewReducer, INITIAL_REVIEW_STATE);
  // Part 1a (2.1 step 0, D24): leave the current review. A pending debounced patch lands on
  // its own draft first, then the review resets and the draft is detached. The draft doc
  // stays resumable from History for 7 days and holds the last edit.
  const clearReview = () => {
    flushDraftReviewPatch();
    dispatchReview({ type: 'RESET' });
    draftRunIdRef.current = null;
  };
  // PlannedStepsTable's count menu (2.3). The reducer ignores a count outside 1 to 1024, a
  // represent row, and a review that is not routed.
  const setRowCandidates = (rowId, candidates) => dispatchReview({ type: 'SET_ROW_CANDIDATES', rowId, candidates });

  // Pilot gating, UX pre-check ONLY: counts today's runs from this browser's local
  // history, so it can undercount (another seat/browser) - the authoritative org-wide
  // cap is enforced server-side at job submit (H17, plan_limits.py / planLimits.js);
  // a denied submit surfaces the server's 429 message.
  //
  // D8 review fix (durability audit 2026-08-20, batch C review CRITICAL-1): a
  // draft run doc (status 'draft', written the moment a plan is produced,
  // not when it's actually executed) is a real history row but must never
  // count as a "run" here. Before this fix, a pilot user who reviewed (but
  // never generated) five plans in a day hit "Daily run limit reached" with
  // zero compute ever having run, and the lockout survived a page refresh
  // too, since loadRuns() seeds history from Firestore with no status
  // filter either. Server-side plan_limits enforcement (X-Spectra-Run-Id on
  // an actual generation submit) is untouched by this, and untouched by
  // drafts in general: a draft never reaches runRows()/a model service, so
  // it was never counted there to begin with.
  const todayStr = new Date().toISOString().slice(0, 10);
  const runsToday = history.filter(r => r.status !== 'draft' && r.ts && r.ts.slice(0, 10) === todayStr).length;
  const dailyCapReached = isPilot && runsToday >= planLimits.dailyRunCap;

  // Services — default base URLs
  const defaultMgUrl = process.env.REACT_APP_MATTERGEN_API || 'http://localhost:8010';
  const defaultIbmUrl = process.env.REACT_APP_IBM_API || 'http://localhost:8011';
  const analyzeBaseUrl = process.env.REACT_APP_ANALYZE_BASE_URL || process.env.REACT_APP_ANALYZE_API || 'http://localhost:8012';
  const defaultDftUrl = process.env.REACT_APP_DFT_BASE_URL || 'http://localhost:8013';
  const defaultQeUrl = process.env.REACT_APP_QE_DFT_BASE_URL || 'http://localhost:8015';
  const defaultLatticeUrl = process.env.REACT_APP_LATTICE_BASE_URL || 'http://localhost:8016';
  const defaultGt4sdUrl = process.env.REACT_APP_GT4SD_API || 'http://localhost:8014';
  const proxyBaseUrl = (process.env.REACT_APP_GPT_API || 'http://localhost:8020/api/chat').replace(/\/api\/chat$/, '');
  const toAbs = (base, p) => {
    if (!p) return '';
    if (/^https?:/i.test(String(p))) return p;
    return String(p).startsWith('/') ? `${base}${p}` : `${base}/${p}`;
  };

  // BYOC (Bring Your Own Compute) — override base URLs when org has active BYOC config
  const [byocActive, setByocActive] = useState(false);
  const [byocEndpoints, setByocEndpoints] = useState({});
  const [byocAuthHeaders, setByocAuthHeaders] = useState(null);

  // Custom models (per-org, verified) — a selected custom model wins over BYOC/direct
  const [customModels, setCustomModels] = useState([]);        // verified models for this org
  // The manual form's MatterGen custom-model pick (Part 1a, 2.4): a manual-form field like
  // the other manual* state. It reaches manual runs only, never an NL run or a Re-run, and
  // 1b retires it with MattergenForm.
  const [manualMGCustomModelId, setManualMGCustomModelId] = useState(null);

  useEffect(() => {
    if (!orgId) { setCustomModels([]); return; }
    import('../firebase/customModels').then(({ listCustomModels }) =>
      listCustomModels(orgId).then(all => setCustomModels(all.filter(m => m.status === 'verified'))).catch(() => {})
    );
  }, [orgId]);

  useEffect(() => {
    if (!orgId) { setByocActive(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { getByocEndpoints } = await import('../firebase/byoc');
        const result = await getByocEndpoints(orgId);
        if (cancelled) return;
        if (result.active) {
          setByocActive(true);
          setByocEndpoints(result.endpoints);
        } else {
          setByocActive(false);
          setByocEndpoints({});
        }
      } catch (e) {
        console.error('[byoc] failed to load config:', e);
        if (!cancelled) { setByocActive(false); setByocEndpoints({}); }
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  // Keep auth headers fresh for relay calls
  useEffect(() => {
    if (!byocActive || !user) { setByocAuthHeaders(null); return; }
    let cancelled = false;
    const refreshToken = async () => {
      try {
        const token = await user.getIdToken();
        if (!cancelled) setByocAuthHeaders({ Authorization: `Bearer ${token}` });
      } catch (e) {
        console.error('[byoc] token refresh failed:', e);
      }
    };
    refreshToken();
    // Refresh every 50 minutes (tokens expire at 60)
    const interval = setInterval(refreshToken, 50 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [byocActive, user]);

  // Part 1a (2.4): the MatterGen base URL and auth headers for the BYOC relay or the built-in
  // default; no manual pick. runRows builds every row's endpoint from row.customModelId
  // (rowEndpoint), so these remain only as the runners' fallback props and the ResultsTable
  // fallback base. Deterministic, no LLM.
  const mgBaseUrl = computeCustomModelBaseUrl({ modelId: null, proxyBaseUrl, byocActive, byocRelayKey: 'mattergen', byocEndpoints, defaultBaseUrl: defaultMgUrl });
  const mgAuthHeaders = computeCustomModelAuthHeaders({ modelId: null, orgId, byocActive, byocAuthHeaders });
  // Part 1a (1.7, B4): headers for every /models/:id result-file fetch. They carry X-Org-Id
  // whether the run reached the custom model through the manual pick or through auto-routing;
  // mgAuthHeaders, the BYOC-or-default value, never does.
  const modelsAuthHeaders = customModelResultHeaders({ orgId, byocAuthHeaders });
  // Read by useToolsRunner through a ref at call time (rowEndpoint, prepareForRun).
  const familyRouting = useMemo(() => ({
    customModels, orgId, proxyBaseUrl,
    byocActive, byocEndpoints, byocAuthHeaders,
    families: {
      mattergen: { byocRelayKey: 'mattergen', defaultBaseUrl: defaultMgUrl },
      fm4m:      { byocRelayKey: 'fm4m',      defaultBaseUrl: defaultIbmUrl },
      gt4sd:     { byocRelayKey: 'gt4sd',     defaultBaseUrl: defaultGt4sdUrl },
    },
  }), [customModels, orgId, proxyBaseUrl, byocActive, byocEndpoints, byocAuthHeaders, defaultMgUrl, defaultIbmUrl, defaultGt4sdUrl]);
  const ibmBaseUrl = byocActive && byocEndpoints.fm4m ? `${proxyBaseUrl}/relay/fm4m` : defaultIbmUrl;
  const dftBaseUrl = byocActive && byocEndpoints.dft ? `${proxyBaseUrl}/relay/dft` : defaultDftUrl;
  const qeBaseUrl = byocActive && byocEndpoints.qe ? `${proxyBaseUrl}/relay/qe` : defaultQeUrl;
  const gt4sdBaseUrl = byocActive && byocEndpoints.gt4sd ? `${proxyBaseUrl}/relay/gt4sd` : defaultGt4sdUrl;

  // Saved candidates
  const [savedCandidates, setSavedCandidates] = useState([]);
  // Stable fingerprint — must match _fpFrame in AppShell.jsx (lattice-based, not SG-based)
  const _fingerprint = (frame) => {
    if (frame.type === 'molecule') return `mol_${frame.smiles}`;
    const abc = frame.lattice_abc;
    if (abc && abc.length === 3) {
      const [a, b, c] = abc.map(v => (typeof v === 'number' ? v.toFixed(2) : v));
      return `${frame.formula}_L:${a}_${b}_${c}`;
    }
    return `${frame.formula}_N:${frame.n || 0}`;
  };
  const isSaved = (frame) => savedCandidates.some(c => c.fingerprint === _fingerprint(frame));
  const saveCandidate = (frame, cifUrl, trajUrl, source, cachedAnalyses) => {
    const fp = _fingerprint(frame);
    if (savedCandidates.some(c => c.fingerprint === fp)) return;
    // Use the candidate's own run ID if available, fall back to latest
    const runId = source?.run_id || frame?.source?.run_id || latestRunIdRef.current;
    const modelId = source?.service || frame?.source?.service || 'unknown';
    const entry = { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, savedAt: new Date().toISOString(), fingerprint: fp, runId, modelId, frame, cifUrl: cifUrl || null, trajUrl: trajUrl || null, source: source || null, cachedAnalyses: cachedAnalyses || {} };
    setSavedCandidates(prev => [entry, ...prev]);
    if (orgId) {
      dbSetSavedFlag(orgId, runId, fp, true, user?.uid).catch(e => console.error('[db] setSavedFlag failed:', e));
      // Persist full candidate data so it survives hard refresh.
      // Reconstruct elements/positions from atomLines for Firestore storage.
      const elements = frame.atomLines?.map(l => (l.split(/\s+/)[0] || '').match(/^[A-Z][a-z]?/)?.[0]).filter(Boolean) || frame.elements || [];
      const positions = frame.atomLines?.map(l => { const t = l.split(/\s+/); return [Number(t[1]), Number(t[2]), Number(t[3])]; }) || frame.positions || [];
      const candidateDoc = {
        formula: frame.formula || null,
        // H13: molecules carry identity in smiles — without it top-level, the
        // candidate doc fingerprinted to undefined and saved molecules clobbered
        // each other under .../candidates/undefined.
        smiles: frame.smiles || null,
        n: frame.n || elements.length || null,
        elements: elements.length > 0 ? elements : null,
        positions: positions.length > 0 ? positions : null,
        lattice_abc: frame.lattice_abc || null,
        lattice_matrix: frame.lattice_matrix || null,
        space_group: frame.spaceGroup || frame.space_group || null,
        composition: frame.composition || null,
        cif: frame.cif || null,
        score: frame.score ?? null,
        rank: frame.rank ?? null,
        source: source || frame.source || null,
        frame,
        mp_match: frame.mp_match || null,
      };
      dbPushRunCandidates(orgId, runId, [candidateDoc]).catch(e => console.error('[db] pushRunCandidates (save) failed:', e));
    }
    if (orgId && cachedAnalyses && Object.keys(cachedAnalyses).length > 0) {
      dbSaveAnalyses(orgId, fp, cachedAnalyses, { uid: user?.uid, userEmail: user?.email, runId }).catch(e => console.error('[db] saveAnalyses (initial) failed:', e));
    }
  };

  // Persist an analysis result to Firestore for a saved candidate (no in-memory caching — loaded fresh from DB on Saved page)
  const updateCandidateAnalyses = (fingerprint, analysesMap) => {
    if (orgId) dbSaveAnalyses(orgId, fingerprint, analysesMap, { uid: user?.uid, userEmail: user?.email, runId: latestRunIdRef.current }).catch(e => console.error('[db] saveAnalyses failed:', e));
  };

  // Bulk-capture generated candidates into runs/{runId}/candidates, the flywheel's
  // raw feedstock (structures for every generated material, not just saved ones).
  // CONSENT-GATED (spec 2026-10-07 Part 0.5): only when the org the run is written
  // to records dataPooling true. effectiveOrg is that org for every session,
  // including superadmin sessions (the SUPERADMIN_ORG_ID doc); there is no
  // superadmin bypass. firestore.rules enforces the same flag server-side on every
  // doc written here (framesToCandidateDocs marks them pooledCapture: true).
  // CUSTOMER INFRASTRUCTURE (Privacy Policy section 9, R4-9): results whose dispatch
  // base is a BYOC /relay/ path or an endpoint-mode (or unknown) custom model are
  // never pooled, and neither is a result with no recorded base.
  // De-duped per (runId, batchKey) for the session so re-opening the results modal
  // doesn't rewrite the batch.
  const bulkPersistedRef = useRef(new Set());
  const persistGeneratedCandidates = (frames, { batchKey = '', cifTexts, source, base } = {}) => {
    const runId = latestRunIdRef.current;
    if (!orgId || !runId || !frames?.length) return;
    if (effectiveOrg?.dataPooling !== true) return;
    if (!isPoolableDispatchTarget(dispatchTargetForBase(base, customModels))) return;
    const key = `${runId}::${batchKey}`;
    if (bulkPersistedRef.current.has(key)) return;
    bulkPersistedRef.current.add(key);
    const docs = dbFramesToCandidateDocs(frames, { cifTexts, source });
    if (docs.length > 0) {
      dbPushRunCandidates(orgId, runId, docs).catch(e => console.error('[db] pushRunCandidates (bulk) failed:', e));
    }
  };
  const unsaveCandidate = (id) => {
    const entry = savedCandidates.find(c => c.id === id);
    setSavedCandidates(prev => prev.filter(c => c.id !== id));
    if (orgId && entry?.fingerprint) {
      dbSetSavedFlag(orgId, null, entry.fingerprint, false, user?.uid)
        .catch(e => console.error('[db] setSavedFlag (unsave) failed:', e));
    }
  };
  const updateCandidateStage = (fingerprint, stage) => {
    setSavedCandidates(prev => prev.map(c =>
      c.fingerprint === fingerprint ? { ...c, stage } : c
    ));
    if (orgId) {
      dbSetCandidateStage(orgId, fingerprint, stage, user?.uid)
        .catch(e => console.error('[db] setCandidateStage failed:', e));
    }
  };

  // Candidate notes — keyed by fingerprint → array of messages, persisted to Firestore
  const [candidateNotes, setCandidateNotes] = useState({});
  const addCandidateNote = (fingerprint, text) => {
    const uid = user?.uid;
    if (!uid || !text.trim()) return;
    const msg = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
      uid, userEmail: user?.email || null, text, createdAt: new Date().toISOString(),
    };
    setCandidateNotes(prev => ({ ...prev, [fingerprint]: [...(prev[fingerprint] || []), msg] }));
    if (orgId) dbAddNoteMessage(orgId, fingerprint, { uid, userEmail: user?.email, text }).catch(e => console.error('[db] addNoteMessage failed:', e));
  };
  const editCandidateNote = (fingerprint, messageId, text) => {
    setCandidateNotes(prev => ({
      ...prev,
      [fingerprint]: (prev[fingerprint] || []).map(m =>
        m.id === messageId ? { ...m, text, editedAt: new Date().toISOString() } : m
      ),
    }));
    if (orgId) dbEditNoteMessage(orgId, fingerprint, messageId, text).catch(e => console.error('[db] editNoteMessage failed:', e));
  };
  const deleteCandidateNote = (fingerprint, messageId) => {
    setCandidateNotes(prev => ({
      ...prev,
      [fingerprint]: (prev[fingerprint] || []).filter(m => m.id !== messageId),
    }));
    if (orgId) dbDeleteNoteMessage(orgId, fingerprint, messageId).catch(e => console.error('[db] deleteNoteMessage failed:', e));
  };
  // Returns all messages for a candidate (array)
  const getCandidateNotes = (fingerprint) => candidateNotes[fingerprint] || [];

  // Modal state
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonModalTitle, setJsonModalTitle] = useState('');
  const [jsonModalText, setJsonModalText] = useState('');
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileModalTitle, setFileModalTitle] = useState('');
  const [fileModalBody, setFileModalBody] = useState('');
  const [fileModalUrl, setFileModalUrl] = useState('');
  const [fileModalMode, setFileModalMode] = useState('text');
  const [fileModalTruncated, setFileModalTruncated] = useState(false);
  const [fileModalMeta, setFileModalMeta] = useState(null);

  // Manual config state
  const [manualModels, setManualModels] = useState({ mattergen: true, fm4m: false, gt4sd: false });
  const [manualMolecularCategory, setManualMolecularCategory] = useState('small_molecule');
  const [manualElements, setManualElements] = useState('');
  const [manualProperty, setManualProperty] = useState('');
  const [manualPropertyValue, setManualPropertyValue] = useState('');
  const [manualNumCandidates, setManualNumCandidates] = useState(64);
  const [manualSmiles, setManualSmiles] = useState('');
  const [manualMGPretrainedOverride, setManualMGPretrainedOverride] = useState('');
  const [manualMGElementsOut, setManualMGElementsOut] = useState('');
  const [manualMGDimensionality, setManualMGDimensionality] = useState('unknown');
  const [manualMGStructureHints, setManualMGStructureHints] = useState('');
  const [manualMGGuidance, setManualMGGuidance] = useState('');
  const [manualMGRecordTraj, setManualMGRecordTraj] = useState(true);
  const [manualMGChunkSize, setManualMGChunkSize] = useState(64);
  const [manualFMModel, setManualFMModel] = useState('mol-xl');
  const [manualFMReturnMode, setManualFMReturnMode] = useState('file');
  const [manualFMMaxItemsInline, setManualFMMaxItemsInline] = useState(10);
  const [manualFMChunkSize, setManualFMChunkSize] = useState(25);

  // GT4SD manual config
  const [manualGT4SDAlgorithm, setManualGT4SDAlgorithm] = useState('regression_transformer');
  const [manualGT4SDAlgorithmVersion, setManualGT4SDAlgorithmVersion] = useState('');
  const [manualGT4SDTargetSmiles, setManualGT4SDTargetSmiles] = useState('');
  // GT4SD RT Part D: left empty, not defaulted to logP. The pairing rule treats a chosen
  // property with no value as an error, and an untouched form must not show one. The
  // requirement is enforced by the row contract and marked in the form instead.
  const [manualGT4SDTargetProperty, setManualGT4SDTargetProperty] = useState('');
  const [manualGT4SDTargetValue, setManualGT4SDTargetValue] = useState('');
  const [manualGT4SDNumSamples, setManualGT4SDNumSamples] = useState(10);

  // Abort control
  const [abortController, setAbortController] = useState(null);

  // Persist toolResults and plan when they change
  useEffect(() => {
    try {
      if (toolResults) localStorage.setItem('tool_results_v1', JSON.stringify(toolResults));
      else localStorage.removeItem('tool_results_v1');
    } catch (_) {}
  }, [toolResults]);
  useEffect(() => {
    try {
      if (resultsRun) localStorage.setItem('results_run_v1', JSON.stringify(resultsRun));
      else localStorage.removeItem('results_run_v1');
    } catch (_) {}
  }, [resultsRun]);

  // Auto-navigate to /results when toolResults first becomes populated
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    if (toolResults) navigate('/results');
  }, [toolResults]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    try {
      if (plan) localStorage.setItem('plan_v1', JSON.stringify(plan));
      else localStorage.removeItem('plan_v1');
    } catch (_) {}
  }, [plan]);

  // Load Firestore data when user signs in — and migrate any legacy localStorage data
  useEffect(() => {
    if (!user?.uid || !orgId) return;
    // #35 — Cancellation guard prevents stale data overwrites on rapid user/org switches
    let cancelled = false;

    // Load runs from Firestore (org-scoped — all users' runs visible)
    dbLoadRuns(orgId).then(runs => {
      if (cancelled) return;
      if (runs.length > 0) { setHistory(runs); return; }
      // Firestore empty — migrate from localStorage if anything is there
      try {
        const j = localStorage.getItem('run_history_v1');
        const legacy = j ? JSON.parse(j) : [];
        if (legacy.length > 0) {
          setHistory(legacy);
          legacy.forEach(rec => dbPushRun(orgId, { ...rec, uid: user.uid, userEmail: user.email }).catch(() => {}));
          localStorage.removeItem('run_history_v1');
        }
      } catch (_) {}
    }).catch(e => console.error('[db] loadRuns failed:', e));

    // Load saved candidates (org-scoped — all saved candidates across all users/runs)
    dbLoadSavedCandidates(orgId).then(docs => {
      if (cancelled) return;
      if (docs.length > 0) {
        const entries = docs.map(d => {
          const isMol = d.domain === 'molecular' || !!d.smiles;
          const fr = d.frame || {};
          const frame = {
            ...fr,
            type: fr.type || (isMol ? 'molecule' : 'crystal'),
            formula: fr.formula || d.formula || null,
            n: fr.n || d.n || null,
            smiles: fr.smiles || d.smiles || null,
            lattice_abc: fr.lattice_abc || d.lattice_abc || null,
            lattice_matrix: fr.lattice_matrix || d.lattice_matrix || null,
            elements: fr.elements || d.elements || null,
            positions: fr.positions || d.positions || null,
            space_group: fr.space_group || fr.spaceGroup || d.space_group || null,
            composition: fr.composition || d.composition || null,
            cif: fr.cif || d.cif || null,
            props: fr.props || d.props || {},
            source: fr.source || d.source || null,
            score: fr.score ?? d.score ?? null,
            rank: fr.rank ?? d.rank ?? null,
          };
          return {
            id: `saved_${d.fingerprint}`,
            savedAt: d.savedAt || null,
            fingerprint: d.fingerprint,
            runId: d.runId || null,
            modelId: d.source?.service || 'unknown',
            frame,
            cifUrl: d.source?.cif_url || d.source?.cifUrl || null,
            trajUrl: d.source?.traj_url || d.source?.trajUrl || null,
            source: d.source || null,
            cachedAnalyses: d.analyses || {},
            stage: d.stage || null,
          };
        });
        setSavedCandidates(entries);
        return;
      }
      // Migrate from localStorage
      try {
        const j = localStorage.getItem('saved_candidates_v1');
        const legacy = j ? JSON.parse(j) : [];
        if (legacy.length > 0) {
          setSavedCandidates(legacy);
          legacy.forEach(entry => {
            if (entry.cachedAnalyses && Object.keys(entry.cachedAnalyses).length > 0) {
              dbSaveAnalyses(orgId, entry.fingerprint, entry.cachedAnalyses, { uid: user.uid, userEmail: user.email }).catch(() => {});
            }
          });
        }
      } catch (_) {}
    }).catch(e => console.error('[db] loadSavedCandidates failed:', e));

    // Load notes (org-scoped — message thread per candidate)
    dbLoadNotes(orgId).then(notes => {
      if (cancelled) return;
      if (Object.keys(notes).length > 0) { setCandidateNotes(notes); return; }
      // Migrate from localStorage (ancient single-text format)
      try {
        const j = localStorage.getItem('candidate_notes_v1');
        const legacy = j ? JSON.parse(j) : {};
        if (Object.keys(legacy).length > 0) {
          const converted = {};
          Object.entries(legacy).forEach(([fp, text]) => {
            converted[fp] = [{ id: `migrated_${user.uid}`, uid: user.uid, userEmail: user.email, text, createdAt: new Date().toISOString() }];
          });
          setCandidateNotes(converted);
          Object.entries(legacy).forEach(([fp, text]) => dbAddNoteMessage(orgId, fp, { uid: user.uid, userEmail: user.email, text }).catch(() => {}));
          localStorage.removeItem('candidate_notes_v1');
        }
      } catch (_) {}
    }).catch(e => console.error('[db] loadNotes failed:', e));

    return () => { cancelled = true; };
  }, [user?.uid, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effects: load persisted history (legacy localStorage fallback while db loads)
  useEffect(() => {
    try { const j = localStorage.getItem('run_history_v1'); if (j) setHistory(JSON.parse(j)); } catch (_) {}
  }, []);
  // Tools runner
  const { runRows, prepareForRun, cancelAll, acRef } = useToolsRunner({ mattergenRef, fm4mRef, gt4sdRef, abortController, setProgress, setToolResults, familyRouting, modelPolicy, planLimits });

  // Chemistry validation helpers
  const { isValidElement } = useChemistry();
  const parseElements = (s) => s.split(',').map((t) => t.trim()).filter(Boolean);
  // Settled decision 5 in manual mode (Part 1a, D16): a model the org disabled or weighted
  // to zero has no checkbox and no form (ManualFormPanel), and its tick is masked here, so the
  // validation gate, the error objects and the manual rows all see the same models.
  const manualPolicy = normalizeModelPolicy(modelPolicy);
  const isRunnable = (svc) => !!manualPolicy[svc]?.enabled && manualPolicy[svc].weight > 0;
  const effectiveManualModels = {
    mattergen: !!manualModels.mattergen && isRunnable('mattergen'),
    fm4m: !!manualModels.fm4m && isRunnable('fm4m'),
    gt4sd: !!manualModels.gt4sd && isRunnable('gt4sd'),
  };
  const mgErrors = {};
  if (effectiveManualModels.mattergen) {
    const g = manualMGGuidance === '' ? '' : Number(manualMGGuidance);
    if (manualMGGuidance !== '' && !(Number.isFinite(g) && g >= 0.1 && g <= 10)) mgErrors.guidance = 'Guidance must be 0.1–10';
    // 0.7: ladder membership (8 to min(256, plan max)), the same menu MattergenForm renders.
    const mgChunkErr = mattergenChunkError(manualMGChunkSize, planLimits.maxCandidatesPerRun);
    if (mgChunkErr) mgErrors.chunkSize = mgChunkErr;
    const nc = Number(manualNumCandidates);
    const maxCand = planLimits.maxCandidatesPerRun;
    if (!(Number.isInteger(nc) && nc >= 1 && nc <= maxCand)) mgErrors.numCandidates = `Candidates must be 1–${maxCand}${isPilot ? ' (pilot)' : ''}`;
    if (manualPropertyValue !== '' && !manualProperty) mgErrors.propertyValue = 'Select a property for the target value';
    if (manualProperty) {
      const pv = Number(manualPropertyValue);
      if (!Number.isFinite(pv)) mgErrors.propertyValue = 'Enter a numeric value';
    }
    const inElems = parseElements(manualElements);
    const outElems = parseElements(manualMGElementsOut);
    if (inElems.length > 30) mgErrors.elements = 'Too many elements (max 30)';
    if (outElems.length > 30) mgErrors.elementsOut = 'Too many elements (max 30)';
    if (inElems.length > 0 && inElems.some((e) => !isValidElement(e))) mgErrors.elements = (mgErrors.elements ? mgErrors.elements + '; ' : '') + 'Invalid element symbols';
    if (outElems.length > 0 && outElems.some((e) => !isValidElement(e))) mgErrors.elementsOut = (mgErrors.elementsOut ? mgErrors.elementsOut + '; ' : '') + 'Invalid element symbols';
    const overlap = inElems.filter((e) => outElems.includes(e));
    if (overlap.length > 0) mgErrors.elementsOut = (mgErrors.elementsOut ? mgErrors.elementsOut + '; ' : '') + `Excluded overlaps with included: ${overlap.join(', ')}`;
  }
  const fmErrors = {};
  if (effectiveManualModels.fm4m) {
    const toks = manualSmiles.split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean);
    if (toks.length === 0) fmErrors.smiles = 'Enter at least one SMILES or upload a file';
    if (toks.length > 1000) fmErrors.smiles = (fmErrors.smiles ? fmErrors.smiles + '; ' : '') + 'Too many SMILES (max 1000)';
    const fmCs = Number(manualFMChunkSize);
    if (!(Number.isInteger(fmCs) && fmCs >= 1 && fmCs <= 200)) fmErrors.chunkSize = 'Chunk size must be 1–200';
    if (manualFMReturnMode === 'inline') {
      const mi = Number(manualFMMaxItemsInline);
      if (!(Number.isInteger(mi) && mi >= 1 && mi <= 1000)) fmErrors.maxItemsInline = 'Max items inline must be 1–1000';
    }
  }
  // Part 1a (2.3): GT4SD ticked with another model dispatches its own row, so its count and
  // its Regression Transformer pair are validated too. GT4SDForm renders both keys.
  const gtErrors = {};
  if (effectiveManualModels.gt4sd) {
    const ns = Number(manualGT4SDNumSamples);
    const maxSamples = planLimits.maxCandidatesPerRun;
    if (!(Number.isInteger(ns) && ns >= 1 && ns <= maxSamples)) gtErrors.numSamples = `Samples must be a whole number from 1 to ${maxSamples}`;
    if (manualGT4SDAlgorithm === 'regression_transformer') {
      const hasProperty = !!manualGT4SDTargetProperty;
      const hasValue = manualGT4SDTargetValue !== '' && manualGT4SDTargetValue != null && Number.isFinite(Number(manualGT4SDTargetValue));
      // Part D: the field's min and max attributes constrain only the spinner, and
      // nothing here submits a form or calls checkValidity(), so a typed or pasted
      // value has to be range-checked here or it reaches Execute unexamined. The range
      // error is reported ahead of the pairing error because the number itself is
      // wrong either way, and that is the more actionable of the two.
      const [rtLow, rtHigh] = GT4SD_RT_TARGET_RANGE;
      const value = Number(manualGT4SDTargetValue);
      if (hasValue && (value < rtLow || value > rtHigh)) {
        gtErrors.targetValue = `Target value must be a logP from ${rtLow} to ${rtHigh}`;
      } else if (hasProperty !== hasValue) {
        gtErrors.targetValue = 'Set the target property and a numeric target value together';
      }
    }
  }
  // The GT4SD-alone gate stays (D15): GT4SD runs only next to MatterGen or FM4M until 1b.
  const hasManualErrors = (!effectiveManualModels.mattergen && !effectiveManualModels.fm4m)
    || (effectiveManualModels.mattergen && Object.values(mgErrors).some(Boolean))
    || (effectiveManualModels.fm4m && Object.values(fmErrors).some(Boolean))
    || (effectiveManualModels.gt4sd && Object.values(gtErrors).some(Boolean));

  // UI helpers
  const openPlan = () => {
    if (plan) {
      try { setJsonModalTitle('Plan JSON'); setJsonModalText(JSON.stringify(plan, null, 2)); setJsonModalOpen(true); }
      catch (_) { setJsonModalTitle('Plan JSON'); setJsonModalText('Invalid plan'); setJsonModalOpen(true); }
    }
  };
  const openSelectionPreview = () => {
    try {
      const fam = Array.isArray(plan?.generation_spec?.system?.family) ? plan.generation_spec.system.family : [];
      const regimeName = fam.includes('inorganic_crystal') ? 'generic_inorganic' : 'generic_molecular';
      const objectives = Array.isArray(plan?.generation_spec?.targets) ? plan.generation_spec.targets : [];
      setJsonModalTitle('Selection Objectives Preview');
      setJsonModalText(JSON.stringify({ regime: regimeName, objectives }, null, 2));
      setJsonModalOpen(true);
    } catch (_) {
      setJsonModalTitle('Selection Objectives Preview');
      setJsonModalText('Unable to compute objectives preview.');
      setJsonModalOpen(true);
    }
  };
  // Fetch wrapper that adds auth headers when the URL goes through the relay (BYOC) or a
  // custom-model dispatch (/models/:id) — I8: custom-model result-file fetches previously got
  // a bare fetch and 401'd silently into the binary-download stub.
  // CRI-13: everything else (e.g. a past run's extxyz artifact served straight off the
  // MatterGen service) still needs the signed-in user's Firebase ID token, so route through
  // `authedFetch`; relay/custom-model headers, when present, take precedence over it.
  // Part 1a (B4): /models/ URLs get modelsAuthHeaders, so an auto-routed model's files load too.
  const byocFetch = (url, opts) => {
    const hdrs = authHeadersForUrl(url, { relayHeaders: byocAuthHeaders, modelsHeaders: modelsAuthHeaders });
    return authedFetch(url, { ...opts, headers: { ...(opts?.headers || {}), ...(hdrs || {}) } });
  };

  const openFile = async ({ title, url, meta }) => {
    const t = title || 'File';
    const u = url || '';
    setFileModalTitle(t);
    setFileModalUrl(u);
    setFileModalMode('loading');
    setFileModalBody('');
    setFileModalTruncated(false);
    setFileModalMeta(meta || null);
    setFileModalOpen(true);
    // Molecule data passed via meta — no file fetch needed
    if (meta?.molecules && Array.isArray(meta?.smiles)) {
      setFileModalMode('text');
      setFileModalBody('');
      return;
    }
    // DB-loaded candidate data passed via meta — no file fetch needed
    if (meta?.dbCandidates) {
      setFileModalMode('text');
      setFileModalBody('');
      return;
    }
    try {
      const lower = String(u).toLowerCase();
      if (lower.endsWith('.zip')) {
        setFileModalMode('binary');
        setFileModalBody('');
        return;
      }
      const fetchTextWithRetry = async (url, attempts = 3, delayMs = 500) => {
        for (let i = 0; i < attempts; i += 1) {
          try { const res = await byocFetch(url); if (res.ok) return await res.text(); }
          catch (_) {}
          if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs));
        }
        throw new Error('Failed to fetch');
      };
      const text = await fetchTextWithRetry(u);
      const MAX = 200000;
      const truncated = text.length > MAX;
      const body = truncated ? text.slice(0, MAX) : text;
      setFileModalMode('text');
      setFileModalBody(body);
      setFileModalTruncated(truncated);
    } catch (e) {
      // I7c: retries exhausted — every fetch attempt failed (auth/CORS/404/network). This used
      // to silently become a 'binary' download stub, masking the real failure (including I8's
      // un-authed /models/ 401s before Task 1). Surface it distinctly instead.
      setFileModalMode('error');
      setFileModalBody(fileModalErrorMessage(e));
      setFileModalTruncated(false);
    }
  };

  /**
   * Open crystals from Firestore instead of fetching the extxyz file.
   * Loads candidates from DB, passes them via meta for instant rendering.
   * Falls back to fetching the files if DB is empty (e.g. fresh run, write not yet flushed).
   * Spec 0.7: `jobs` lists EVERY MatterGen job of the variant ({ url, runId, candidates,
   * cifs, trajectories }, from ResultsTable's groupMgRuns). The fallback fetches all of them
   * and merges them before parsing, ranking and bulk capture. Without `jobs`, the single
   * `url` is fetched as before.
   */
  const openCrystals = async ({ title, url, meta, mgRunIds, jobs }) => {
    setFileModalTitle(title || 'Crystals');
    setFileModalUrl(url || '');
    setFileModalMode('loading');
    setFileModalBody('');
    setFileModalTruncated(false);
    // Carry the service run ids so the fresh-parse path can attribute bulk-captured
    // candidates to their generating service run (flywheel provenance).
    setFileModalMeta((meta || mgRunIds) ? { ...(meta || {}), mgRunIds: mgRunIds || null } : null);
    setFileModalOpen(true);
    // Spec 0.7: every job of the variant that has an extxyz file.
    const jobList = Array.isArray(jobs) ? jobs.filter((j) => j && j.url) : [];
    const showDbCandidates = (candidates) => {
      setFileModalMeta({ ...meta, dbCandidates: candidates });
      setFileModalMode('text');
      setFileModalBody('');
    };
    let partialDb = []; // this variant's candidates when Firestore holds only part of it

    const runId = latestRunIdRef.current;
    if (orgId && runId) {
      try {
        const loaded = await dbLoadRunCandidates(orgId, runId);
        // Collect candidates from all services (mattergen, investigate, etc.)
        let allCandidates = [];
        for (const entry of Object.values(loaded || {})) {
          if (Array.isArray(entry?.candidates)) allCandidates.push(...entry.candidates);
        }
        // Filter to specific MatterGen run IDs if provided (variant-level filtering).
        // Spec 0.7: bulk capture stamps EVERY job id of the variant on source.run_ids
        // (ordinary saves carry no service run id), so match that list as well as a single
        // source.run_id. Before this, a reopen never matched a bulk-captured candidate.
        const ids = new Set(Array.isArray(mgRunIds) ? mgRunIds : []);
        const fromVariant = (c) => {
          const src = c?.source || {};
          if (src.run_id && ids.has(src.run_id)) return true;
          return Array.isArray(src.run_ids) && src.run_ids.some((id) => ids.has(id));
        };
        const filtered = ids.size > 0 ? allCandidates.filter(fromVariant) : allCandidates;
        // Firestore wins only when it holds the whole variant: at least as many candidates
        // as its jobs generated. A partial set (a capture that failed part-way, or one
        // written before every job was captured) falls through to fetching every job.
        const expected = jobList.reduce((sum, j) => sum + (Number(j.candidates) || 0), 0);
        if (filtered.length > 0 && filtered.length >= expected) {
          showDbCandidates(filtered);
          return;
        }
        partialDb = filtered;
      } catch (e) {
        console.warn('[openCrystals] DB load failed, falling back to URL fetch:', e.message);
      }
    }
    if (jobList.length > 0) {
      // Every job's extxyz, complete frames only, concatenated in job order. The CIF and
      // trajectory lists are re-indexed to the merged frames, so frame i keeps its own
      // job's files. Not truncated: every frame is parsed, shown and captured.
      const merged = await loadExtxyzJobs(jobList, byocFetch);
      if (merged.frameCount > 0) {
        setFileModalMeta({
          ...(meta || {}),
          mgRunIds: mgRunIds || null,
          cifs: merged.cifs,
          trajectories: merged.trajectories,
          extxyzUrls: jobList.map((j) => j.url),
          failedJobs: merged.failed,
        });
        setFileModalUrl(jobList[0].url);
        setFileModalBody(merged.text);
        setFileModalTruncated(false);
        setFileModalMode('text');
        return;
      }
      // No job file could be fetched: a partial Firestore set beats an error.
      if (partialDb.length > 0) {
        showDbCandidates(partialDb);
        return;
      }
      setFileModalMode('error');
      setFileModalBody(fileModalErrorMessage(new Error(merged.failed[0]?.error || 'Failed to fetch')));
      setFileModalTruncated(false);
      return;
    }
    // Fallback: fetch extxyz from URL (fresh run or DB miss)
    openFile({ title, url, meta });
  };

  // ── Run persistence: start / progress / finish (D1) ────────────────────────
  // A generation run used to reach Firestore exactly once, via a single
  // pushHistory() call AFTER the whole run resolved: a tab close at any
  // point before that, even 95% through a long multi-variant run, lost the
  // whole run with zero durable trace (durability audit D1). These three
  // functions replace that single call with the write-before-compute pattern
  // dft-service/lattice-service already use server-side: startRunDoc() writes
  // the run doc the moment a run begins (status 'running', the parsed spec,
  // executed rows, uid, started_at), before runRows() dispatches any row;
  // persistRunProgress() appends that step's results as each one settles
  // (wired into runRows via its onStep callback), and finishRunDoc() marks
  // the run terminal ('complete', 'error', or 'cancelled') honestly once it
  // settles. All three extend the exact schema pushRun()/loadRuns() already
  // read and write: a run doc looks identical whether it was written the
  // old atomic way (investigate mode, still using pushRun directly) or
  // through this three-phase path; only `status` and the timing differ.

  // A user cancel (the Cancel button, AbortController.abort()) throws the
  // same shape a real failure does; every catch block below already tests
  // for it (to suppress the error banner) using this same predicate, so
  // reuse it here too: a deliberate stop must land as 'cancelled', not
  // 'error', or History permanently mislabels it as a failure.
  const isAbortError = (err) => !!(err && (err.name === 'AbortError' || String(err).toLowerCase().includes('abort')));
  // D8 (durability audit 2026-08-20) added the optional `status` param:
  // 'running' (the default, unchanged for every pre-existing caller) writes
  // a real run the moment compute is about to start; 'draft' writes the
  // plan-review window itself durable the moment the plan is produced,
  // before the user has even seen the planned-steps table, let alone
  // clicked Generate. Same doc shape either way, just a different starting
  // status.
  const startRunDoc = (planObj, extra = {}, status = 'running') => {
    // 0.1 (editable-plan spec, 2026-10-07): this id is also the run's
    // X-Spectra-Run-Id (every caller passes it to runRows as opts.runId), so it
    // must keep matching the services' RUN_ID_RE, ^[A-Za-z0-9_.:-]{1,64}$, or
    // each job submit would count as its own run against the daily cap.
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const ts = new Date().toISOString();
    const rec = {
      id, ts,
      // Local heartbeat, mirroring the Firestore doc's serverTimestamp()
      // updatedAt. Without this, deriveRunStatus() falls back to `ts` (the
      // run's START time) for staleness, so any run whose own total
      // duration exceeds STALE_AFTER_MS renders 'interrupted' in the very
      // tab actively running it, with Re-run enabled, duplicate-submission
      // bait on a run that is not interrupted at all.
      updatedAt: ts,
      plan: planObj,
      results: null,
      uid:               user?.uid || null,
      userEmail:         user?.email || null,
      userQuery:         extra.userQuery || planObj?.generation_spec?.meta?.user_prompt_raw || '',
      selectedCategory:  extra.selectedCategory || null,
      classifierResult:  extra.classifierResult || null,
      // Part 1a (1.9): kept for old readers, never computed; executedRows record what ran.
      routerCalls:       [],
      mode:              extra.mode || 'nl',
      status,
      candidateCount:    0,
      // Part 1a (1.9): the execution fields ride on the record when the caller has them.
      // executedBy is the executing user, written exactly when executedRows is.
      ...(extra.reviewState ? { reviewState: extra.reviewState } : {}),
      ...(Array.isArray(extra.executedRows) ? { executedRows: extra.executedRows, executedBy: user?.uid || null } : {}),
      ...(extra.rankingWeights ? { rankingWeights: extra.rankingWeights } : {}),
      ...(extra.rowsReconstructed === true ? { rowsReconstructed: true } : {}),
    };
    setHistory((prev) => [rec, ...prev].slice(0, 50));
    if (status !== 'draft') {
      // Write-before-compute: set the recovery handle and render the row
      // BEFORE the caller dispatches any chunk to a model service. A draft
      // has no compute and no results yet -- D14's "latest run" pointer
      // must keep meaning an actual run, not a plan still under review, so
      // this is skipped for status 'draft' and set instead at promotion
      // time (see the executePlannedSteps draft-promotion path below).
      latestRunIdRef.current = id;
      try { localStorage.setItem('latest_run_id_v1', id); } catch (_) {}
    }
    if (orgId) dbStartRun(orgId, rec).catch(e => console.error('[db] startRun failed:', e));
    return id;
  };

  // D8: promote the plan-review window's own draft doc to a real run when
  // Generate is clicked, reusing its id -- never a second doc for the same
  // run. Falls back to a fresh startRunDoc() when there's no draft to
  // promote (draftRunIdRef.current null): every caller that never reviews a
  // plan first (manual runs, rerunRun) behaves exactly as it did before D8.
  // Part 1a (1.9, D20): the promotion patch carries every execution field (the
  // executed spec, the final serialized review, the ranking weights, the
  // executed rows and executedBy). It never carries uid, so a teammate who
  // resumes a draft does not take it over. The local row also gets
  // plan.generation_spec, which viewHistoryRun prefers.
  const promoteDraftOrStartRunDoc = (planObj, extra) => {
    const draftId = draftRunIdRef.current;
    if (!draftId) return startRunDoc(planObj, extra);
    draftRunIdRef.current = null;
    cancelDraftReviewPatch(); // the promotion patch below carries the final review
    const ts = new Date().toISOString();
    const patch = {
      status: 'running',
      generationSpec: planObj.generation_spec,
      reviewState: extra.reviewState,
      rankingWeights: extra.rankingWeights,
      executedRows: extra.executedRows,
      executedBy: user?.uid || null,
    };
    setHistory((prev) => prev.map((r) => (r.id === draftId ? {
      ...r,
      ...patch,
      plan: { ...(r.plan || {}), generation_spec: cloneJson(planObj.generation_spec) },
      updatedAt: ts,
    } : r)));
    latestRunIdRef.current = draftId;
    try { localStorage.setItem('latest_run_id_v1', draftId); } catch (_) {}
    if (orgId) dbPatchRun(orgId, draftId, patch).catch(e => console.error('[db] patchRun (promote draft) failed:', e));
    return draftId;
  };

  // D8: keep the draft's review in step as the user edits it during the review
  // window, so a resumed draft restores exactly what the user had. Part 1a (2.1
  // step 5): the patch is { reviewState: serializeReviewState(review) }, and the
  // same object goes onto the local history row. Debounced (600 ms) so several
  // edits in quick succession write once; active only while a draft is open
  // (draftRunIdRef set by the Route's draft write or by resumeDraft) and the
  // review is a draft stage (selectIsDraft).
  useEffect(() => {
    cancelDraftReviewPatch(); // a newer review supersedes whatever was pending
    const draftId = draftRunIdRef.current;
    if (!draftId || !selectIsDraft(review)) return undefined;
    if (skipNextReviewPatchRef.current) {
      // The review is exactly what the doc already holds (see the ref's comment).
      skipNextReviewPatchRef.current = false;
      return undefined;
    }
    const reviewState = serializeReviewState(review);
    const run = () => {
      setHistory((prev) => prev.map((r) => (r.id === draftId ? { ...r, reviewState, updatedAt: new Date().toISOString() } : r)));
      if (orgId) dbPatchRun(orgId, draftId, { reviewState }).catch(e => console.error('[db] patchRun (draft review) failed:', e));
    };
    const timer = setTimeout(() => { draftReviewPatchRef.current = null; run(); }, 600);
    draftReviewPatchRef.current = { timer, run };
    return cancelDraftReviewPatch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review, orgId]);

  // Wired into runRows' onStep callback: fires after the MatterGen rows
  // settle and after each FM4M or GT4SD row, appending that step's
  // results to the run doc rather than waiting for the whole run to finish.
  const persistRunProgress = (runId, partialResults) => {
    const candidateCount = partialResults
      ? Object.values(partialResults).reduce((sum, svc) => sum + (Number(svc?.total_requested) || 0), 0)
      : 0;
    // Refresh the local heartbeat too, same reason as startRunDoc's
    // updatedAt: staleness is judged off this field, and a long-running
    // run's own tab must never see itself as stale mid-run.
    const updatedAt = new Date().toISOString();
    setHistory((prev) => prev.map((r) => (r.id === runId ? { ...r, results: partialResults, candidateCount, updatedAt } : r)));
    if (orgId) dbPatchRun(orgId, runId, { toolResults: partialResults, candidateCount }).catch(e => console.error('[db] patchRun (progress) failed:', e));
  };

  // Finalize a run doc after runRows() settles, success or failure. On a
  // failure the caller passes err.toolResults, which is undefined when every
  // MatterGen row failed or the run was cancelled, so the patch never clobbers
  // the partial toolResults persistRunProgress already wrote: then only
  // `status` changes.
  const finishRunDoc = (runId, resultsObj, status = 'complete') => {
    try {
      const hasResults = resultsObj !== undefined;
      const candidateCount = hasResults && resultsObj
        ? Object.values(resultsObj).reduce((sum, svc) => sum + (Number(svc?.total_requested) || 0), 0)
        : undefined;
      const finishTs = new Date().toISOString();
      setHistory((prev) => prev.map((r) => (r.id === runId ? {
        ...r,
        status,
        ...(hasResults ? { results: resultsObj } : {}),
        ...(candidateCount !== undefined ? { candidateCount } : {}),
      } : r)));
      if (status === 'complete') {
        setToolResultsTs(finishTs);
        try { localStorage.setItem('tool_results_ts_v1', finishTs); } catch (_) {}
      }
      if (orgId) {
        dbPatchRun(orgId, runId, {
          status,
          ...(hasResults ? { toolResults: resultsObj } : {}),
          ...(candidateCount !== undefined ? { candidateCount } : {}),
        }).catch(e => console.error('[db] patchRun (finish) failed:', e));
      }
    } catch (_) {}
  };

  // View a historical run — loads results from Firestore if not in memory
  // D8 (durability audit 2026-08-20): opens the plan-review window back up
  // for a draft run doc (HistoryTable's "Resume" action on a 'draft'-status
  // row), restoring the persisted review rather than making the user retype
  // the query and re-review from scratch. draftRunIdRef is set to the SAME
  // doc's id so a later Generate click promotes it in place instead of
  // writing a second draft.
  // Part 1a (2.3 Resume): the stored reviewState when it restores (every row
  // revalidated); a legacy draft (no reviewState) is routed once under the
  // current policy, plan max and custom models, with its own budgetOverrides,
  // and marked reconstructed. Results on screen and another mode would hide
  // the review table, so both are cleared (map 11.7).
  const resumeDraft = (h) => {
    const resolvedPlan = h.plan || (h.generationSpec ? { generation_spec: h.generationSpec } : null);
    if (!resolvedPlan) return;
    flushDraftReviewPatch(); // a pending edit lands on the draft it belongs to, never on this one
    const restored = restoreReviewState(h.reviewState) || rebuildLegacyReview(h, { customModels, modelPolicy, planMax });
    const restore = { type: 'RESTORE', state: restored };
    // A review the reducer would refuse changes nothing (and leaves no stale skip flag).
    if (!restored || reviewReducer(INITIAL_REVIEW_STATE, restore) === INITIAL_REVIEW_STATE) return;
    draftRunIdRef.current = h.id || null;
    // The restored review is what the doc holds; a rebuilt legacy draft is persisted by its
    // first edit instead (1.11), so either way no patch is due now.
    if (h.id) skipNextReviewPatchRef.current = true;
    dispatchReview(restore);
    setPlan(resolvedPlan);
    setToolResults(null);
    setResultsRun(null);
    setMode('nl');
    lastSubmitRef.current = {
      userQuery: h.userQuery || '',
      classifierResult: h.classifierResult || null,
      selectedCategory: h.selectedCategory || null,
      mode: h.mode || 'nl',
    };
    navigate('/search');
  };

  const viewHistoryRun = async (h) => {
    // Firestore run docs store generationSpec, not plan — reconstruct the plan shape
    const resolvedPlan = h.plan || (h.generationSpec ? { generation_spec: h.generationSpec } : null);
    setPlan(resolvedPlan);
    setToolResultsTs(h.ts || null);
    clearReview();
    // Part 1a (D36): the results about to show are this run's: its executed rows (none
    // for a run recorded before 1a) and its ranking weights (the built-in ones when the
    // doc has none or they do not validate).
    setResultsRun({
      runId: h.id || null,
      rows: Array.isArray(h.executedRows) ? h.executedRows : [],
      rankingWeights: validRankingWeights(h.rankingWeights) || BUILTIN_RANKING_WEIGHTS,
    });
    // Track run ID so openCrystals can load candidates from this run
    // Also persist to localStorage so page refresh restores the correct run (CRI-8 fix)
    if (h.id) {
      latestRunIdRef.current = h.id;
      try { localStorage.setItem('latest_run_id_v1', h.id); } catch (_) {}
    }
    // Auto-open crystal viewer for investigate runs loaded from history
    if (h.mode === 'investigate') investigateAutoOpenRef.current = true;
    // Try in-memory results first, then Firestore toolResults field, then load candidates
    const storedResults = h.results || h.toolResults || null;
    // Detect stripped investigate toolResults: investigate key exists but candidates were removed
    // before writing to Firestore (via _stripToolResults) — must re-fetch from DB (CRI-8 fix)
    const isStrippedInvestigate = (
      storedResults &&
      storedResults.investigate &&
      !Array.isArray(storedResults.investigate?.candidates)
    );
    if (storedResults && !isStrippedInvestigate) {
      setToolResults(storedResults);
    } else if (orgId && h.id) {
      setLoading(true);
      setLoadingMessage('Loading results...');
      try {
        const loaded = await dbLoadRunCandidates(orgId, h.id);
        if (loaded) {
          // Remap 'unknown' service key to 'investigate' for investigate-mode runs
          // (older candidates may have been stored without source.service)
          if (h.mode === 'investigate' && !loaded.investigate && loaded.unknown) {
            loaded.investigate = loaded.unknown;
            delete loaded.unknown;
          }
          // For investigate runs: merge stripped metadata (runs array) with loaded candidates
          if (isStrippedInvestigate && loaded.investigate && storedResults?.investigate) {
            const stripped = storedResults.investigate;
            if (stripped.runs && !loaded.investigate.runs?.length) {
              loaded.investigate.runs = stripped.runs;
            }
            if (stripped.ok !== undefined) loaded.investigate.ok = stripped.ok;
          }
          setToolResults(loaded);
          // Re-trigger auto-open since toolResults is now populated after the async load
          if (h.mode === 'investigate') investigateAutoOpenRef.current = true;
        } else {
          setToolResults(storedResults || null);
        }
      } catch (e) {
        console.error('[db] viewHistoryRun load failed:', e);
        setToolResults(storedResults || null);
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    } else {
      setToolResults(storedResults || null);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Submit / execute / rerun
  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (dailyCapReached) {
      setError(`Daily run limit reached (${runsToday}/${planLimits.dailyRunCap}). Runs reset at midnight UTC. Contact sales to upgrade.`);
      return;
    }
    // Part 1a (2.1 step 0): a stale routed review can never resurface under the new plan,
    // and no render of this submit can patch an earlier draft.
    clearReview();
    investigateAutoOpenRef.current = false;
    setLoading(true);
    setLoadingMessage('Decomposing...');
    const ac = new AbortController();
    setAbortController(ac);
    acRef.current = ac; // #36 — sync ref immediately
    try {
      if (mode === 'nl') {
        // D17: only a submit that will run clears what is on screen. The NL path always
        // reaches a review, so it clears up front; the manual path clears below, after
        // its backstop accepts at least one row.
        setPlan(null);
        setToolResults(null);
        setResultsRun(null);
        // ── Stage 1: resolve domains to scope the Stage 2 prompt ──────────────
        // Three paths (in priority order):
        //   a. Category chip selected by user → use its domains directly (free)
        //   b. Stage 1 LLM classifier         → cheap ~256-token call
        //   c. Keyword inference fallback      → no LLM, brittle but always works
        let preDomains = null;
        let classifierResult = null; // Full Stage 1 result for persistence

        if (selectedCategory) {
          // Path a: user already told us the domain via chip
          const cat = getFilteredCategories(allowedCategories).find(c => c.id === selectedCategory);
          if (cat) {
            preDomains = cat.domains;
            classifierResult = { categories: [selectedCategory], domains: cat.domains, confidence: 'high', source: 'chip' };
          }
        }

        if (!preDomains) {
          // Path b: Stage 1 LLM classifier (robust, handles ambiguous queries)
          setLoadingMessage('Classifying domain...');
          classifierResult = await classifyDomains(input, ac.signal, allowedCategories);
          preDomains = classifierResult?.domains || null;
          console.debug('[Stage1] classifier result:', classifierResult, '→ preDomains:', preDomains);
        }

        if (!preDomains || preDomains.length === 0) {
          // Path c: keyword inference fallback
          const fallback = resolveSpec({ description: input, meta: { user_prompt_raw: input }, system: {} });
          preDomains = fallback.meta.resolved_domains || ['inorganic_crystal'];
          classifierResult = { categories: [], domains: preDomains, confidence: 'low', source: 'keyword_fallback' };
          console.debug('[Stage1] keyword fallback → preDomains:', preDomains);
        }

        // ── Stage 2: scoped decomposer prompt ─────────────────────────────────
        setLoadingMessage('Decomposing...');
        const scopedModels = MODEL_REGISTRY.filter(m => m.domains.some(d => preDomains.includes(d)));
        const systemInstruction = buildDecomposerPrompt(scopedModels.length > 0 ? scopedModels : MODEL_REGISTRY);

        const text = await askLLM({ systemInstruction, userText: input, signal: ac.signal, response_format: { type: 'json_object' } });
        let parsed = normalizeDecomposerOutput(parseLlmJson(text));
        if (!parsed) {
          console.error('[Stage2] decomposer returned unparseable JSON:', (text || '').slice(0, 500));
          setError('Model did not return valid JSON.');
          return;
        }
        const normalized = normalizeParsedWithObjectives(parsed);
        if (!normalized || !normalized.generation_spec || typeof normalized.generation_spec !== 'object' || Array.isArray(normalized.generation_spec)) {
          console.error('[Stage2] decomposer response missing generation_spec:', (text || '').slice(0, 500));
          setError('Model did not return valid JSON.');
          return;
        }
        // Inject pre_classified_domains so specResolver fast-paths over keyword inference
        if (!normalized.generation_spec.meta) normalized.generation_spec.meta = {};
        normalized.generation_spec.meta.pre_classified_domains = preDomains;
        // C5 — whitelist LLM-emitted meta keys BEFORE resolveSpec; drops any
        // mattergen_options/fm4m_options/gt4sd_options/model_path/seed_smiles the
        // decomposer emitted, which would otherwise override the router's choice.
        normalized.generation_spec.meta = sanitizeNlMeta(normalized.generation_spec.meta);
        normalized.generation_spec = resolveSpec(normalized.generation_spec);
        // 0.4: clamp the per-domain candidate budget to the plan max for every tier, so the
        // planned-steps preview never offers more than the plan allows. routeAndPrefill
        // then fits the whole RUN total (fitRunTotal), which this clamp alone cannot do.
        if (normalized.generation_spec?.constraints?.num_candidates > planLimits.maxCandidatesPerRun) {
          normalized.generation_spec.constraints.num_candidates = planLimits.maxCandidatesPerRun;
        }
        setPlan(normalized);
        // Store enriched submit context for when executePlannedSteps starts/finishes the run
        lastSubmitRef.current = { userQuery: input, classifierResult, selectedCategory, mode: 'nl' };
        // Part 1a (2.1 steps 2 and 3): Route once. routeAndPrefill returns the rows weighted,
        // snapped and fitted (R4-1: the preview shows what will run), so the router snapshot
        // is taken after every automatic adjustment and only expert edits differ from it.
        const spec = normalized.generation_spec;
        const { rows, skipped } = routeAndPrefill(spec, { modelPolicy, customModels, planMax });
        const understood = {
          type: 'UNDERSTOOD', decomposerSnapshot: cloneJson(spec), spec: cloneJson(spec),
          classifierSource: classifierResult?.source ?? null, rankingWeights: BUILTIN_RANKING_WEIGHTS,
          proposedRankingWeights: BUILTIN_RANKING_WEIGHTS, proposedRankingSource: 'builtin',
        };
        const routed = {
          type: 'ROUTED', spec,
          routerSnapshot: { rows: cloneJson(rows), skipped, reconstructed: false }, rows, unsteerableTargets: [],
        };
        dispatchReview(understood);
        dispatchReview(routed);
        // D8 (durability audit 2026-08-20): the plan-review window itself is
        // durable from the moment the plan is produced: a draft run doc, written
        // through the same startRunDoc path status 'running' uses (same doc
        // shape, an earlier status); executePlannedSteps promotes THIS SAME doc
        // to 'running' on Generate rather than creating a second one. Part 1a
        // (2.1 step 4): it carries the serialized review, exactly the state just
        // dispatched.
        const next = reviewReducer(reviewReducer(INITIAL_REVIEW_STATE, understood), routed);
        draftRunIdRef.current = startRunDoc(normalized, { ...lastSubmitRef.current, reviewState: serializeReviewState(next) }, 'draft');
        skipNextReviewPatchRef.current = true; // the review just written is the dispatched one
      } else {
        setLoadingMessage('Preparing spec...');
        // Part 1a (2.3): the manual form is the input of one pure adapter. manualFormToRows
        // assembles every row with buildRow and normalizes every form value into the 1a
        // bounds (D39); the ticks are the policy-masked ones (D16), and the MatterGen pick is
        // the form's own field (2.4). Numeric fields go in as the raw form values.
        const form = {
          models: { ...effectiveManualModels },
          elementsIn: parseElements(manualElements),
          elementsOut: parseElements(manualMGElementsOut),
          dimensionality: manualMGDimensionality,
          structureHints: parseElements(manualMGStructureHints),
          property: manualProperty,
          propertyValue: manualPropertyValue,
          numCandidates: manualNumCandidates,
          mattergen: { checkpoint: manualMGPretrainedOverride, guidance: manualMGGuidance, recordTrajectories: Boolean(manualMGRecordTraj), chunkSize: manualMGChunkSize, customModelId: manualMGCustomModelId },
          fm4m: { category: manualMolecularCategory, seeds: manualSmiles.split(/\r?\n|,/), model: manualFMModel, returnMode: manualFMReturnMode, maxItemsInline: manualFMMaxItemsInline, chunkSize: manualFMChunkSize },
          gt4sd: { algorithm: manualGT4SDAlgorithm, algorithmVersion: manualGT4SDAlgorithmVersion, targetSmiles: manualGT4SDTargetSmiles, targetProperty: manualGT4SDTargetProperty, targetValue: manualGT4SDTargetValue, numSamples: manualGT4SDNumSamples },
        };
        const { rows, spec } = manualFormToRows(form, { customModels, modelPolicy, planMax });
        // The Execute-time backstop; nothing runnable writes no run doc (D17).
        const prepared = prepareForRun(rows, spec.targets);
        if (prepared.rows.length === 0) {
          setError(nothingRunnableMessage(prepared));
          return;
        }
        setToolResults(null);
        setResultsRun(null);
        const parsed = { generation_spec: spec, sanity_checks: [], ambiguities: { missing_critical_info: [], assumptions_made: [] } };
        setPlan(parsed);
        lastSubmitRef.current = { userQuery: '', selectedCategory: null, classifierResult: null, mode: 'manual' };
        const runId = startRunDoc(parsed, { ...lastSubmitRef.current, executedRows: prepared.rows, rankingWeights: BUILTIN_RANKING_WEIGHTS });
        setResultsRun({ runId, rows: prepared.rows, rankingWeights: BUILTIN_RANKING_WEIGHTS });
        try {
          const results = await runRows(rows, spec, { runId, onStep: (partial) => persistRunProgress(runId, partial), prepared });
          finishRunDoc(runId, results, 'complete');
        } catch (runErr) {
          finishRunDoc(runId, runErr?.toolResults, isAbortError(runErr) ? 'cancelled' : 'error');
          throw runErr;
        }
      }
    } catch (err) {
      const msg = String(err);
      if (err && (err.name === 'AbortError' || msg.toLowerCase().includes('abort'))) setError(''); else setError(msg);
    } finally {
      setLoading(false);
      setLoadingMessage('');
      setAbortController(null);
    }
  };

  // NL Execute (Part 1a, 2.1 step 6): runs exactly the reviewed rows. The runnable check
  // comes before anything on screen is cleared; when nothing can run the draft stays a
  // draft and no run doc is promoted.
  const executePlannedSteps = async () => {
    setError('');
    if (dailyCapReached) {
      setError(`Daily run limit reached (${runsToday}/${planLimits.dailyRunCap}). Runs reset at midnight UTC. Contact sales to upgrade.`);
      return;
    }
    if (!selectCanExecute(review)) return;
    const prepared = prepareForRun(review.rows, review.spec?.targets);
    if (prepared.rows.length === 0) {
      setError(nothingRunnableMessage(prepared));
      return;
    }
    setToolResults(null);
    setResultsRun(null);
    setLoading(true);
    setLoadingMessage('Executing planned steps...');
    const ac2 = new AbortController();
    setAbortController(ac2);
    acRef.current = ac2; // #36 — sync ref immediately
    // D8: promote this plan's own draft doc (written at Route) to 'running' instead of
    // starting a second doc for the same run. Part 1a (1.9): the promotion carries the
    // executed spec, the final review, the prepared rows and the ranking weights.
    const runId = promoteDraftOrStartRunDoc({ generation_spec: review.spec }, {
      ...lastSubmitRef.current,
      reviewState: serializeReviewState(review),
      executedRows: prepared.rows,
      rankingWeights: review.rankingWeights,
    });
    // D20: plan follows the spec that ran (the same content in 1a; 1c lets them differ).
    setPlan((p) => ({ ...(p || {}), generation_spec: cloneJson(review.spec) }));
    setResultsRun({ runId, rows: prepared.rows, rankingWeights: review.rankingWeights });
    try {
      const results = await runRows(review.rows, review.spec, { runId, onStep: (partial) => persistRunProgress(runId, partial), prepared });
      finishRunDoc(runId, results, 'complete');
      dispatchReview({ type: 'RESET' });
    } catch (err) {
      // The review stays for a retry, which starts a fresh doc: the draft was promoted.
      finishRunDoc(runId, err?.toolResults, isAbortError(err) ? 'cancelled' : 'error');
      const msg = String(err);
      if (!isAbortError(err)) setError(msg);
    } finally {
      setLoading(false);
      setLoadingMessage('');
      setAbortController(null);
    }
  };

  // Re-run (Part 1a, 1.11): replays exactly the rows the source run executed. A run recorded
  // before 1a has none, so its spec is routed once, with the source doc's own legacy
  // budgetOverrides (never this session's, B3) and never the manual pick (2.4). The
  // runnable check comes before anything on screen is cleared: a refused Re-run leaves the
  // results showing and says why (D17). Re-run docs carry no reviewState and are never
  // logged; the current plan max still bounds them through the prepareForRun backstop, and
  // a custom model no longer verified is rejected on its row, never re-routed.
  const rerunRun = async (h) => {
    if (dailyCapReached) {
      setError(`Daily run limit reached (${runsToday}/${planLimits.dailyRunCap}). Runs reset at midnight UTC. Contact sales to upgrade.`);
      return;
    }
    setError('');
    if (!h || typeof h !== 'object') return;
    let spec = h.generationSpec || h.plan?.generation_spec || {};
    const reconstructed = !(Array.isArray(h.executedRows) && h.executedRows.length > 0);
    const rankingWeights = validRankingWeights(h.rankingWeights) || BUILTIN_RANKING_WEIGHTS;
    const ctx = {
      customModels,
      modelPolicy,
      planMax,
      budgetOverrides: (h.budgetOverrides && typeof h.budgetOverrides === 'object') ? h.budgetOverrides : {},
    };
    let rows;
    let skipped = [];
    if (!reconstructed) {
      // Stored rows map onto today's live checkpoint lists before validation (1.2.6).
      rows = h.executedRows.map((r) => ({ ...normalizeReplayRow(r), dispatchTarget: null, adjustments: {} }));
    } else {
      const legacy = rowsFromLegacyPlan(h.plan || { generation_spec: spec }, ctx);
      spec = legacy.spec;
      rows = legacy.rows;
      skipped = legacy.routerSnapshot?.skipped || [];
    }
    // Revalidates every row (any member can rewrite a run doc) and applies policy and models.
    const prepared = prepareForRun(rows, spec.targets);
    if (prepared.rows.length === 0) {
      setError(nothingRunnableMessage(prepared, skipped));
      return;
    }
    setToolResults(null);
    setResultsRun(null);
    setLoading(true);
    setLoadingMessage('Re-running...');
    const ac = new AbortController();
    setAbortController(ac);
    acRef.current = ac; // #36 — sync ref immediately
    setPlan({ generation_spec: spec }); // map 11.8: Results, Refine and the plan JSON follow the Re-run
    const runId = startRunDoc({ generation_spec: spec }, {
      mode: 'rerun',
      userQuery: h.userQuery || h.description || '', // map 11.16
      executedRows: prepared.rows,
      rankingWeights,
      ...(reconstructed ? { rowsReconstructed: true } : {}),
    });
    setResultsRun({ runId, rows: prepared.rows, rankingWeights });
    try {
      const results = await runRows(rows, spec, { runId, onStep: (partial) => persistRunProgress(runId, partial), prepared });
      finishRunDoc(runId, results, 'complete');
    } catch (err) {
      finishRunDoc(runId, err?.toolResults, isAbortError(err) ? 'cancelled' : 'error');
      const msg = String(err);
      if (!isAbortError(err)) setError(msg);
    } finally {
      setLoading(false);
      setLoadingMessage('');
      setAbortController(null);
    }
  };

  // Refine helpers (used by ResultsTable buttons)
  // Spec 0.7: `r` is the variant's first job; ResultsTable also passes the variant's
  // requested total and the chunk size it ran with (groupMgRuns), which are restored in
  // place of that one job's batch_size x num_batches.
  const refineMattergen = (r, { totalCandidates, chunkSize } = {}) => {
    setMode('manual');
    setManualModels((m) => ({ ...m, mattergen: true }));
    // Part 1a (1.7): the run row's custom model comes back as the manual pick only while it is
    // still a verified MatterGen model of this org (customModels holds verified models only).
    // A model deleted or unverified since the run, another family's model, or a built-in run
    // clears the pick: MattergenForm could neither show nor clear a stale pick, and every Run
    // would reject the row. The checkpoint below is restored from r.target either way.
    const runModelId = typeof r?.customModelId === 'string' ? r.customModelId : null;
    const pickStillVerified = !!runModelId && customModels.some((m) => m.id === runModelId && m.family === 'mattergen');
    setManualMGCustomModelId(pickStillVerified ? runModelId : null);
    try {
      const body = r?.request || {};
      const props = body?.properties || {};
      // Part 1a (contract 1.7): the run row names its checkpoint. A weights-mode custom row
      // sends no model_path or pretrained_name, so the body alone would restore nothing.
      // Runs recorded before 1a have no target and restore from the body, as before.
      if (typeof r?.target === 'string' && MATTERGEN_BUILTIN_CHECKPOINTS.includes(r.target)) setManualMGPretrainedOverride(r.target);
      else if (typeof body?.pretrained_name === 'string') setManualMGPretrainedOverride(body.pretrained_name);
      else {
        // 0.2 / decision 20: the manual form has no model_path field. A run sent with a
        // built-in checkpoint path restores as that checkpoint; any other path is dropped.
        const checkpoint = builtinMattergenCheckpoint(body?.model_path);
        if (checkpoint) setManualMGPretrainedOverride(checkpoint);
      }
      if (typeof props.chemical_system === 'string') {
        const elems = props.chemical_system.split('-').map((s) => s.trim()).filter(Boolean);
        setManualElements(elems.join(','));
      } else if (Array.isArray(plan?.generation_spec?.system?.elements_in)) {
        setManualElements(plan.generation_spec.system.elements_in.join(','));
      }
      if (Array.isArray(plan?.generation_spec?.system?.elements_out)) setManualMGElementsOut(plan.generation_spec.system.elements_out.join(','));
      const sys = plan?.generation_spec?.system || {};
      if (typeof sys.dimensionality === 'string') setManualMGDimensionality(sys.dimensionality);
      if (Array.isArray(sys.structure_hint)) setManualMGStructureHints(sys.structure_hint.join(','));
      if (typeof body?.diffusion_guidance_factor === 'number') setManualMGGuidance(String(body.diffusion_guidance_factor)); else setManualMGGuidance('');
      if (typeof body?.record_trajectories === 'boolean') setManualMGRecordTraj(body.record_trajectories);
      if (typeof props.dft_band_gap === 'number') { setManualProperty('band_gap'); setManualPropertyValue(String(props.dft_band_gap)); }
      else if (typeof props.ml_bulk_modulus === 'number') { setManualProperty('bulk_modulus'); setManualPropertyValue(String(props.ml_bulk_modulus)); }
      else if (typeof props.energy_above_hull === 'number') { setManualProperty('energy_above_hull'); setManualPropertyValue(String(props.energy_above_hull)); }
      else { setManualProperty(''); setManualPropertyValue(''); }
      const total = Number(totalCandidates);
      const num = Number.isFinite(total) && total > 0 ? total : Number(body?.batch_size) * Number(body?.num_batches);
      if (Number.isFinite(num) && num > 0) setManualNumCandidates(num);
      const cs = Number(chunkSize);
      setManualMGChunkSize(Number.isFinite(cs) && cs > 0 ? cs : DEFAULT_MATTERGEN_CHUNK_SIZE);
    } catch (_) {}
    navigate('/search');
  };

  const refineFM4M = (r) => {
    setMode('manual');
    setManualModels((m) => ({ ...m, fm4m: true }));
    try {
      // Part 1a (1.7): the run row records what the row asked for and how it ran. FM4MRunner
      // stamps requestedModel, returnMode, maxItemsInline (inline only), chunkSize and domain
      // on every FmRun. The plan's legacy fm4m_options and family fill only a field the run
      // row lacks (toolResults recorded before 1a).
      const opts = plan?.generation_spec?.meta?.fm4m_options || {};
      const firstString = (...vals) => vals.find((v) => typeof v === 'string' && v !== '');
      const firstPositive = (...vals) => vals.map(Number).find((v) => Number.isFinite(v) && v > 0);
      const model = firstString(r?.requestedModel, r?.request?.model, opts.model);
      if (model) setManualFMModel(model);
      const returnMode = firstString(r?.returnMode, opts.return_mode);
      if (returnMode) setManualFMReturnMode(returnMode);
      const maxItemsInline = firstPositive(r?.maxItemsInline, opts.max_items_inline);
      if (maxItemsInline !== undefined) setManualFMMaxItemsInline(maxItemsInline);
      const chunkSize = firstPositive(r?.chunkSize, opts.chunk_size);
      if (chunkSize !== undefined) setManualFMChunkSize(chunkSize);
      const inputs = r?.request?.inputs;
      if (Array.isArray(inputs) && inputs.length > 0) setManualSmiles(inputs.join('\n'));
      else if (Array.isArray(plan?.generation_spec?.meta?.seed_smiles)) setManualSmiles(plan.generation_spec.meta.seed_smiles.join('\n'));
      if (r?.domain === 'small_molecule' || r?.domain === 'polymer') setManualMolecularCategory(r.domain);
      else {
        const fam = plan?.generation_spec?.system?.family || [];
        if (Array.isArray(fam)) {
          if (fam.includes('polymer')) setManualMolecularCategory('polymer');
          else setManualMolecularCategory('small_molecule');
        }
      }
    } catch (_) {}
    navigate('/search');
  };

  // ── Investigate mode: formula-only (+ optional CIF) analysis run ──
  const investigateAutoOpenRef = useRef(false);
  const onInvestigateSubmit = async ({ formula, cif }) => {
    if (!formula || !formula.trim()) return;
    setError('');
    if (dailyCapReached) {
      setError(`Daily run limit reached (${runsToday}/${planLimits.dailyRunCap}). Runs reset at midnight UTC. Contact sales to upgrade.`);
      return;
    }
    setPlan(null);
    setToolResults(null);
    setResultsRun(null);
    clearReview();
    setLoading(true);
    setLoadingMessage('Looking up structure…');

    const f = formula.trim();
    let resolvedCif = cif ? cif.trim() : null;
    let spaceGroup = null;
    let elements = [];
    let positions = [];
    let latticeMatrix = null;
    let latticeAbc = null;
    let nSites = 0;
    let materialId = null;
    let lookupSource = null;

    const base = analyzeBaseUrl || 'http://localhost:8012';

    if (resolvedCif) {
      // Layer 1a: user pasted a CIF — parse it to extract structural data
      try {
        console.log('[investigate] parsing user-provided CIF at', base);
        const resp = await authedFetch(`${base}/parse/cif`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cif: resolvedCif }),
        });
        if (resp.ok) {
          const parsed = await resp.json();
          if (parsed.found) {
            spaceGroup = parsed.space_group || null;
            elements = parsed.elements || [];
            positions = parsed.positions || [];
            latticeMatrix = parsed.lattice_matrix || null;
            latticeAbc = parsed.lattice_abc || null;
            nSites = parsed.n_sites || elements.length;
            lookupSource = 'user_cif';
          }
        }
      } catch (e) {
        console.warn('[investigate] /parse/cif failed:', e.message);
      }
    } else {
      // Layer 1b: no CIF — generate structure from formula via pymatgen
      try {
        console.log('[investigate] calling /lookup/formula at', base, 'for', f);
        const resp = await authedFetch(`${base}/lookup/formula`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ formula: f }),
        });
        if (resp.ok) {
          const lookup = await resp.json();
          console.log('[investigate] lookup result:', lookup.found, lookup.error || '(no error)');
          if (lookup.found && lookup.cif) {
            resolvedCif = lookup.cif;
            spaceGroup = lookup.space_group || null;
            elements = lookup.elements || [];
            positions = lookup.positions || [];
            latticeMatrix = lookup.lattice_matrix || null;
            latticeAbc = lookup.lattice_abc || null;
            nSites = lookup.n_sites || elements.length;
            materialId = lookup.material_id || null;
            // #58: the service now returns a real Materials Project structure
            // (source "materials_project"), or found=false; never a fabricated one.
            lookupSource = lookup.source || 'materials_project';
          } else if (lookup.error) {
            console.warn('[investigate] structure generation failed:', lookup.error);
          }
        } else {
          console.warn('[investigate] /lookup/formula returned', resp.status, resp.statusText);
        }
      } catch (e) {
        console.warn('[investigate] /lookup/formula network error:', e.message);
      }
    }

    // Layer 2: formula-only fallback (resolvedCif remains null — economic report only)

    if (!resolvedCif) {
      console.warn('[investigate] no CIF resolved for', f, '— proceeding with formula only');
    }

    setLoadingMessage('Creating investigation…');
    const runId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    // Build fingerprint using the same lattice-based format as _fingerprint()
    const fp = latticeAbc && latticeAbc.length === 3
      ? `${f}_L:${latticeAbc.map(v => (typeof v === 'number' ? v.toFixed(2) : v)).join('_')}`
      : `${f}_N:${nSites || 0}`;
    const candidate = {
      id: `inv:${runId}`,
      fingerprint: fp,
      domain: 'inorganic_crystal',
      formula: f,
      score: 0,
      rank: 1,
      n: nSites || 0,
      props: {},
      props_meta: {},
      // Store elements/positions/lattice at top level (candidatesToFrames reads them here).
      // Keep frame flat — Firestore rejects nested arrays (positions, lattice_matrix).
      ...(elements.length > 0 ? { elements, positions } : {}),
      ...(latticeMatrix ? { lattice_matrix: latticeMatrix } : {}),
      ...(latticeAbc ? { lattice_abc: latticeAbc } : {}),
      ...(spaceGroup ? { space_group: spaceGroup } : {}),
      frame: {
        ...(latticeAbc ? { lattice_abc: latticeAbc } : {}),
        ...(spaceGroup ? { space_group: spaceGroup } : {}),
      },
      source: {
        service: 'investigate',
        run_id: runId,
        ...(materialId ? { material_id: materialId } : {}),
        ...(lookupSource ? { lookup_source: lookupSource } : {}),
      },
      ...(resolvedCif ? { cif: resolvedCif } : {}),
    };

    const results = {
      investigate: {
        ok: true,
        candidates: [candidate],
        runs: [{
          request: { formula: f, has_cif: !!resolvedCif, lookup_source: lookupSource },
          response: { run_id: runId, files: { extxyz: '__db__' } },
        }],
      },
    };

    const rec = {
      id: runId,
      ts: new Date().toISOString(),
      plan: null,
      results,
      uid: user?.uid || null,
      userEmail: user?.email || null,
      userQuery: `Investigate: ${f}`,
      selectedCategory: null,
      classifierResult: null,
      routerCalls: [],
      mode: 'investigate',
      candidateCount: 1,
    };

    latestRunIdRef.current = runId;
    try { localStorage.setItem('latest_run_id_v1', runId); } catch (_) {}
    setToolResultsTs(rec.ts);
    setHistory(prev => [rec, ...prev].slice(0, 50));
    if (orgId) {
      dbPushRun(orgId, rec).catch(e => console.error('[db] pushRun (investigate) failed:', e));
      dbPushRunCandidates(orgId, runId, [candidate]).catch(e => console.error('[db] pushRunCandidates (investigate) failed:', e));
    }

    investigateAutoOpenRef.current = true;
    setToolResults(results);
    setLoading(false);
    setLoadingMessage('');
  };

  // Auto-open crystal viewer for investigate runs after navigation
  useEffect(() => {
    if (!investigateAutoOpenRef.current || !toolResults?.investigate) return;
    investigateAutoOpenRef.current = false;
    const candidates = toolResults.investigate.candidates || [];
    if (candidates.length > 0) {
      setTimeout(() => openCrystals({
        title: `Investigate: ${candidates[0].formula}`,
        url: null,
        meta: { dbCandidates: candidates },
        mgRunIds: [],
      }), 200);
    }
  }, [toolResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Context value
  const value = {
    // services
    mgBaseUrl, mgAuthHeaders, modelsAuthHeaders, ibmBaseUrl, analyzeBaseUrl, dftBaseUrl, qeBaseUrl, gt4sdBaseUrl, defaultLatticeUrl, toAbs,
    // BYOC
    byocActive, byocAuthHeaders,
    // Custom models (verified, per-org)
    customModels,
    // core state
    input, setInput,
    plan, setPlan, toolResults, setToolResults, toolResultsTs, setToolResultsTs, history, setHistory,
    resultsRun,
    loading, setLoading, loadingMessage, setLoadingMessage, error, setError,
    progress,
    mode, setMode,
    selectedCategory, setSelectedCategory,
    // plan review (Part 1a)
    review, clearReview, setRowCandidates,
    mattergenRef, fm4mRef, gt4sdRef,
    abortController, setAbortController,
    // manual config
    manualModels, setManualModels,
    manualMGCustomModelId, setManualMGCustomModelId,
    manualMolecularCategory, setManualMolecularCategory,
    manualElements, setManualElements,
    manualProperty, setManualProperty,
    manualPropertyValue, setManualPropertyValue,
    manualNumCandidates, setManualNumCandidates,
    manualSmiles, setManualSmiles,
    manualMGPretrainedOverride, setManualMGPretrainedOverride,
    manualMGElementsOut, setManualMGElementsOut,
    manualMGDimensionality, setManualMGDimensionality,
    manualMGStructureHints, setManualMGStructureHints,
    manualMGGuidance, setManualMGGuidance,
    manualMGRecordTraj, setManualMGRecordTraj,
    manualMGChunkSize, setManualMGChunkSize,
    manualFMModel, setManualFMModel,
    manualFMReturnMode, setManualFMReturnMode,
    manualFMMaxItemsInline, setManualFMMaxItemsInline,
    manualFMChunkSize, setManualFMChunkSize,
    // GT4SD manual config
    manualGT4SDAlgorithm, setManualGT4SDAlgorithm,
    manualGT4SDAlgorithmVersion, setManualGT4SDAlgorithmVersion,
    manualGT4SDTargetSmiles, setManualGT4SDTargetSmiles,
    manualGT4SDTargetProperty, setManualGT4SDTargetProperty,
    manualGT4SDTargetValue, setManualGT4SDTargetValue,
    manualGT4SDNumSamples, setManualGT4SDNumSamples,
    mgErrors, fmErrors, gtErrors, hasManualErrors,
    // pilot tier gating
    isPilot, orgPlan, planLimits, runsToday, dailyCapReached,
    // controllers / helpers
    cancelAll, viewHistoryRun, resumeDraft,
    onSubmit, executePlannedSteps, rerunRun, onInvestigateSubmit,
    openPlan,
    openSelectionPreview,
    openFile, openCrystals,
    // modal state for rendering
    jsonModalOpen, setJsonModalOpen, jsonModalTitle, jsonModalText,
    fileModalOpen, setFileModalOpen, fileModalTitle, fileModalBody, fileModalUrl, fileModalMode, fileModalTruncated, fileModalMeta,
    // saved candidates
    savedCandidates, saveCandidate, unsaveCandidate, isSaved, updateCandidateStage, addCandidateNote, editCandidateNote, deleteCandidateNote, getCandidateNotes, updateCandidateAnalyses,
    // flywheel bulk capture (consent-gated)
    persistGeneratedCandidates,
    // refine helpers for results
    refineMattergen, refineFM4M,
  };

  return (
    <AppContext.Provider value={value}>{children}</AppContext.Provider>
  );
}
